/* =============================================================
   calc-modern.js
   Global, purely-presentational enhancement layer for ALL the
   calculators (fa-collection, fa-product, flavor-trio-deal,
   blc-5pack-deal). Dependency-free.

   It only OBSERVES and ENHANCES. It never calls or redefines any
   of the site's existing functions / pricing logic.

   Features:
   1) Count-up animation on every mapped value display. It animates
      ONLY the text node holding the first number (so child markup
      like <strong> is preserved), preserves the surrounding format
      (currency, decimals, "/mg", "%", commas), and RETARGETS to the
      latest value if it changes mid-animation (so rapid clicks never
      leave a stale number).
   2) Spring/haptic press feedback (navigator.vibrate) on controls.
   3) Subtle pulse on the value tile + CTA bump when totals change.
   4) Re-scan on shopify:section:load (theme editor).
   5) Respects prefers-reduced-motion.

   Defensive: 'use strict', every feature wrapped in try/catch,
   missing selectors no-op. Self-writes are tagged so the observer
   never reacts to them (no feedback loop). Resting value is always
   the site's true value.
   ============================================================= */
(function () {
  'use strict';

  var HAS_DOM = typeof document !== 'undefined';

  var REDUCE = false;
  try {
    REDUCE = typeof window !== 'undefined' && window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) { REDUCE = false; }

  /* Value-display selectors to animate. Per-product/per-slide
     calculators reuse generic class selectors so these cover all
     duplicated instances. */
  var VALUE_SELECTORS = [
    /* fa-bc bundle calculator */
    '#faBcCount', '#faBcPrice', '#faBcOriginal', '#faBcSavings', '#faBcAvgMg',
    '.fa-bc-slide [data-mg-value]', '.fa-bc-slide [data-mg-total]',
    /* fa-mvc multi-variant calculator (ids suffixed per product) */
    '[id^="mvcCount"]', '[id^="mvcPrice"]', '[id^="mvcMg"]', '[id^="mvcCpm"]',
    '.fa-mvc-slide .fa-mvc-price',
    /* fa-product PDP cost-per-mg */
    '#fa-tile-cpm', '#fa-tile-price', '.fa-pdp-price',
    /* flavor-trio-deal */
    '#ftd-subtotal', '#ftd-need', '#ftd-rem', '#ftd-tot',
    /* blc-5pack-deal */
    '#bfp-rem', '#bfp-tot',
    /* homepage carousel slide price (9/14: cpm tiles + #fa-tally bar removed) */
    '.carousel-slide .product-price'
  ];

  /* Control selectors for haptic press feedback. */
  var CONTROL_SELECTORS = [
    '.fa-bc-slide .fa-bc-qty-btn.fa-bc-minus',
    '.fa-bc-slide .fa-bc-qty-btn.fa-bc-plus',
    '.fa-bc-tier', '#faBcAtc', '#faBcClear', '.fa-bc-dot',
    '#faBcEdgePrev', '#faBcEdgeNext', '#faBcPrev', '#faBcNext',
    '.fa-mvc-slide .fa-mvc-qty-btn.fa-mvc-minus',
    '.fa-mvc-slide .fa-mvc-qty-btn.fa-mvc-plus',
    '.fa-mvc-tab', '.fa-mvc-dot', '#mvcPrev', '#mvcNext',
    '[id^="mvcAtc"]', '[id^="mvcClear"]', '[id^="mvcPrev"]', '[id^="mvcNext"]',
    '.fa-pdp-tier', '.fa-pdp-qty button', '#fa-qty', '.fa-pdp-variant-select',
    '.ftd-variant', '.ftd-quick', '#ftd-claim-btn', '#ftd-sc-btn',
    '#bfp-sc-btn', '.bfp-claim-card', '.bfp-tier-clickable',
    /* homepage carousel controls — qty steppers, tier pills, variant
       tiles, per-slide CTAs, and the carousel arrows */
    '.fa-qty-btn', '.hp-tier', '.variant-tile', '.btn-atc',
    '.carousel-btn', '.carousel-btn--prev', '.carousel-btn--next'
  ];
  var CONTROL_SELECTOR_STR = CONTROL_SELECTORS.join(',');

  /* Running-total value selectors whose nearest CTA gets a subtle
     bump when they change. */
  var CTA_BUMP_FOR = {
    '#faBcPrice': '#faBcAtc',
    '#ftd-subtotal': '#ftd-claim-btn'
  };

  /* ---- numeric token helpers ------------------------------- */

  // First numeric token (optional commas / decimals). Returns
  // {value, raw, decimals, index, length, hadCommas} or null.
  function parseNumber(text) {
    if (text == null) return null;
    var m = String(text).match(/-?\d[\d,]*(?:\.\d+)?/);
    if (!m) return null;
    var raw = m[0];
    var clean = raw.replace(/,/g, '');
    var value = parseFloat(clean);
    if (isNaN(value)) return null;
    var dot = clean.indexOf('.');
    var decimals = dot === -1 ? 0 : (clean.length - dot - 1);
    return {
      value: value, raw: raw, decimals: decimals,
      index: m.index, length: raw.length,
      hadCommas: raw.indexOf(',') !== -1
    };
  }

  function formatNumber(value, decimals, withCommas) {
    var fixed = value.toFixed(decimals);
    if (!withCommas) return fixed;
    var parts = fixed.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  }

  // One eased step from cur toward target. Snaps when within half a
  // ULP of the target's precision so it always terminates.
  function stepToward(cur, target, decimals) {
    var eps = decimals > 0 ? Math.pow(10, -decimals) / 2 : 0.5;
    if (Math.abs(target - cur) <= eps) return target;
    return cur + (target - cur) * 0.22;
  }

  /* ---- DOM helpers (browser only) -------------------------- */

  function firstNumberTextNode(el) {
    try {
      if (typeof document === 'undefined' || !document.createTreeWalker) return null;
      var show = (typeof NodeFilter !== 'undefined' && NodeFilter.SHOW_TEXT) || 4;
      var tw = document.createTreeWalker(el, show, null, false);
      var node;
      while ((node = tw.nextNode())) {
        if (/\d/.test(node.nodeValue)) return node;
      }
    } catch (e) {}
    return null;
  }

  function nearestTile(el) {
    try {
      return el.closest(
        '.fa-cpm-tile, .fa-pdp-detail, [data-mg-tile], .fa-bc-summary, ' +
        '.fa-bc-summary-bottles, .fa-mvc-summary, .fa-mvc-slide, ' +
        '.fa-bc-slide, .ftd-card, .bfp-wrap'
      ) || el.parentElement || el;
    } catch (e) { return el; }
  }

  function pulseClass(el, cls, ms) {
    if (REDUCE || !el) return;
    try {
      el.classList.remove(cls);
      void el.offsetWidth; // restart the animation
      el.classList.add(cls);
      window.setTimeout(function () {
        try { el.classList.remove(cls); } catch (e) {}
      }, ms);
    } catch (e) {}
  }

  function bumpCta(selector) {
    if (REDUCE || !selector) return;
    try {
      var cta = document.querySelector(selector);
      if (cta) pulseClass(cta, 'cm-cta-bump', 450);
    } catch (e) {}
  }

  /* ---- count-up animation (text-node, retargeting) --------- */

  function animateValue(el, ctaSelector) {
    try {
      var node = firstNumberTextNode(el);
      if (!node) { el.dataset.cmLast = el.textContent; return; }
      var info = parseNumber(node.nodeValue);
      if (!info) { el.dataset.cmLast = el.textContent; return; }

      // (Re)set the live target + template from the latest text.
      el.__cmNode = node;
      el.__cmTpl = node.nodeValue;
      el.__cmIdx = info.index;
      el.__cmLen = info.length;
      el.__cmDec = info.decimals;
      el.__cmCommas = info.hadCommas || Math.abs(info.value) >= 1000;
      el.__cmTarget = info.value;

      var tile = nearestTile(el);

      if (REDUCE) {
        el.dataset.cmLast = String(info.value);
        pulseClass(tile, 'cm-pulse', 500);
        if (ctaSelector) bumpCta(ctaSelector);
        return;
      }

      pulseClass(tile, 'cm-pulse', 500);
      if (ctaSelector) bumpCta(ctaSelector);

      // Already animating: the running loop will pick up the new
      // target/template above — don't start a second loop.
      if (el.__cmRAF) return;

      var disp = parseFloat(el.dataset.cmLast);
      if (isNaN(disp)) disp = info.value; // first observation: no tween
      el.__cmDisp = disp;

      var step = function () {
        try {
          var target = el.__cmTarget;
          var next = stepToward(el.__cmDisp, target, el.__cmDec);
          el.__cmDisp = next;
          var numStr = formatNumber(next, el.__cmDec, el.__cmCommas);
          var full = el.__cmTpl.slice(0, el.__cmIdx) + numStr +
            el.__cmTpl.slice(el.__cmIdx + el.__cmLen);
          // Write ONLY the number's text node (preserves child markup).
          try { el.__cmNode.nodeValue = full; } catch (e) {}
          // Tag our own write so the observer ignores it (no loop).
          el.__cmExpect = el.textContent;
          if (next === target) {
            el.__cmRAF = null;
            el.dataset.cmLast = String(target);
          } else {
            el.__cmRAF = window.requestAnimationFrame(step);
          }
        } catch (e) { el.__cmRAF = null; }
      };
      el.__cmRAF = window.requestAnimationFrame(step);
    } catch (e) {
      try { el.__cmRAF = null; } catch (e2) {}
    }
  }

  /* ---- observer wiring ------------------------------------- */

  var observed = (typeof WeakSet !== 'undefined') ? new WeakSet() : null;

  function ctaSelectorFor(el) {
    try {
      for (var sel in CTA_BUMP_FOR) {
        if (CTA_BUMP_FOR.hasOwnProperty(sel) && el.matches && el.matches(sel)) {
          return CTA_BUMP_FOR[sel];
        }
      }
    } catch (e) {}
    return null;
  }

  function wireValue(el) {
    try {
      if (!el || (observed && observed.has(el))) return;
      if (observed) observed.add(el);

      // Seed last value so the first real change tweens from it.
      var info = parseNumber(el.textContent);
      el.dataset.cmLast = info ? String(info.value) : el.textContent;
      el.__cmExpect = el.textContent;

      var ctaSelector = ctaSelectorFor(el);

      var obs = new MutationObserver(function () {
        // Ignore our own writes — compare to what we last wrote.
        if (el.textContent === el.__cmExpect) return;
        animateValue(el, ctaSelector);
      });
      obs.observe(el, { childList: true, characterData: true, subtree: true });
    } catch (e) {}
  }

  function scan() {
    if (typeof MutationObserver === 'undefined') return;
    for (var i = 0; i < VALUE_SELECTORS.length; i++) {
      try {
        var nodes = document.querySelectorAll(VALUE_SELECTORS[i]);
        for (var j = 0; j < nodes.length; j++) wireValue(nodes[j]);
      } catch (e) {}
    }
  }

  /* ---- haptic press feedback ------------------------------- */

  function onPointerDown(ev) {
    if (REDUCE) return;
    try {
      var t = ev.target;
      if (!t || !t.closest) return;
      if (t.closest(CONTROL_SELECTOR_STR) && navigator.vibrate) {
        navigator.vibrate(8);
      }
    } catch (e) {}
  }

  function wireHaptics() {
    try {
      document.addEventListener('pointerdown', onPointerDown, { passive: true });
    } catch (e) {
      try { document.addEventListener('pointerdown', onPointerDown); } catch (e2) {}
    }
  }

  /* ---- bootstrap (browser only) ---------------------------- */

  if (HAS_DOM) {
    try {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function () {
          try { scan(); } catch (e) {}
        });
      } else { scan(); }
    } catch (e) {}

    wireHaptics();

    try {
      document.addEventListener('shopify:section:load', function () {
        window.setTimeout(scan, 50);
      });
    } catch (e) {}
  }

  /* ---- node test hook (no effect in the browser) ----------- */
  try {
    if (typeof module !== 'undefined' && module.exports) {
      module.exports = {
        parseNumber: parseNumber,
        formatNumber: formatNumber,
        stepToward: stepToward
      };
    }
  } catch (e) {}
})();
